/**
 * ActivityForm.jsx - "Log Activity", and the same form in place to correct an
 * entry (pass `initial`).
 *
 * Moved out of AccountDetailPanel so the account, the contact (C-01) and the
 * opportunity (AL-01) timelines log the same way. Every field it had stays:
 * Type (with its '-' placeholder), Revision # for a proposal (AL-05), Date,
 * Subject, Notes, the recording LINK, and the optional next step that becomes
 * a task in the same save (AL-06; not on a company with sites, not when the
 * host says hideNextStep - the opportunity's Activity tab, whose task would
 * land on the site - never when editing). Phase 1.2 adds, on every type:
 *   - Customer attendees: the family's contacts, never a Left company one (AL-07, C-04)
 *   - Internal attendees: our people, stored by display name (AL-07)
 *   - Tag to opportunities: the family's, link wins (AL-09)
 *   - Files: several per entry, uploaded one by one once the entry is saved (AL-07)
 *
 * The entry saves first (JSON); each file is then POSTed to
 * /api/crm/activity/<id>/attachments. A file that fails is named, the entry
 * stays saved, and the form stays open: Save again updates that entry and
 * retries only the files that failed - it never logs a second copy. If the
 * form goes away after that without Save or Close (the host panel closed), it
 * still reports the saved entry through onSaved, so the host refreshes.
 *
 * An edit goes to the ENTRY's own account (a site's entry edited from its
 * company used to 404 against the company's id).
 *
 * "Create from screenshot / text" (components/aiCreate) opens a NEW entry
 * with `prefill` (type, date, subject, notes it read) and `initialFiles` (the
 * screenshots, staged like picked files). Both only seed the first form; the
 * person still presses Save, and the form after a save starts blank as before.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useDataScope } from '../../context/DataScope';
import { MEETING_TYPES, REVISION_TYPE, isLeftCompany, pickField } from '../../data/crmStages';
import { INPUT, LABEL, BTN_PRIMARY, BTN_CANCEL, ERROR_LINE } from './crmStyles';
import { taskPayload } from './crmFormHelpers';
import { contactIndex, opportunityLabel } from './nextStep';
import {
  attendeeContacts,
  taggableOpportunities,
  activityBody,
  formFromEntry,
  todayLocal,
  fileAllowed,
  fileSize,
  toggleId,
  withPicked,
  FILE_ACCEPT,
} from './timelineModel';
import { ACTIVITY_FILES_BASE } from './ActivityFiles';
import TagPeople from '../tasks/TagPeople';

const enc = encodeURIComponent;
const ids = (v) => (Array.isArray(v) ? v.map(String) : []);
const blankNext = (me) => ({ description: '', owner: me, due_date: '', tagged: [] });
const blankForm = (me, defaults) => ({
  text: '',
  date: todayLocal(),
  type: '',
  subject: '',
  recording_url: '',
  revision: '',
  contact_ids: ids(defaults && defaults.contact_ids),
  internal_attendees: ids(defaults && defaults.internal_attendees),
  opportunity_ids: ids(defaults && defaults.opportunity_ids),
});

// what a new entry may start with (prefill); anything else is ignored
const PREFILL_KEYS = ['type', 'date', 'subject', 'text'];
const prefillOf = (p) =>
  Object.fromEntries(PREFILL_KEYS.filter((k) => p && typeof p[k] === 'string' && p[k].trim()).map((k) => [k, p[k].trim()]));

const readError = async (r, fallback) => {
  const b = await r.json().catch(() => ({}));
  return b.error || fallback;
};

const PICK_BOX = {
  border: '1px solid #E0E0E0',
  borderRadius: 6,
  background: '#fff',
  padding: '5px 8px',
  maxHeight: 118,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};
const PICK_ROW = { display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, cursor: 'pointer', padding: '2px 0', minWidth: 0 };
const SMALL_BTN = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  fontSize: 10,
  fontWeight: 800,
  fontFamily: 'Montserrat',
};

/** A labelled checkbox list (the multi-pickers are never a '-' select - see crmPhase12.render.test). */
function CheckList({ label, empty, options, picked, onToggle }) {
  return (
    <div style={{ minWidth: 0 }}>
      <label style={LABEL}>{label}</label>
      <div role="group" aria-label={label} style={PICK_BOX}>
        {options.length === 0 ? (
          <div style={{ fontSize: 10.5, color: '#999', padding: '3px 0' }}>{empty}</div>
        ) : (
          options.map((o) => (
            <label key={o.id} style={PICK_ROW}>
              <input type="checkbox" checked={picked.includes(String(o.id))} onChange={() => onToggle(o.id)} />
              <span
                style={{ fontWeight: 600, color: '#0D0D0D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={o.label}
              >
                {o.label}
              </span>
              {o.hint && <span style={{ color: o.extra ? '#B07503' : '#999', fontSize: 9.5, whiteSpace: 'nowrap' }}>{o.hint}</span>}
            </label>
          ))
        )}
      </div>
    </div>
  );
}

export default function ActivityForm({
  record,
  defaults,
  companyWithSites,
  hideNextStep,
  onSaved,
  onCancel,
  initial,
  prefill,
  initialFiles,
}) {
  const { allAccounts, allProjects, users } = useApp();
  const { user } = useAuth();
  const scope = useDataScope();
  const me = (user && user.name) || '';
  const editing = !!(initial && initial.id);
  // what the form opened with: an already-picked id the lists below do not
  // offer (a contact who has since left, an opportunity linked elsewhere) is
  // still shown, ticked, so saving never drops it without anyone seeing
  const [origin] = useState(() => (editing ? formFromEntry(initial) : blankForm(me, defaults)));
  const [form, setForm] = useState(() => ({ ...origin, ...(editing ? {} : prefillOf(prefill)), next: blankNext(me) }));
  // File objects, uploaded after the save
  const [staged, setStaged] = useState(() => (editing ? [] : (initialFiles || []).filter((f) => f && fileAllowed(f.name))));
  const [removing, setRemoving] = useState([]); // ids of this entry's files to delete on save (edit)
  const [saved, setSaved] = useState(null); // the entry, once saved with some files still to go
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // a partly saved entry nobody has been told about yet; reported on unmount
  // if the form is dropped without Save or Close, so the host still refreshes
  const unreported = useRef(null);
  const onSavedRef = useRef(onSaved);
  useEffect(() => {
    onSavedRef.current = onSaved;
  }, [onSaved]);
  useEffect(
    () => () => {
      const out = unreported.current;
      unreported.current = null;
      if (out && onSavedRef.current) onSavedRef.current(out);
    },
    []
  );

  const contactOptions = useMemo(() => {
    const idx = contactIndex(allAccounts);
    const list = attendeeContacts(record, allAccounts).map((c) => ({ id: c.id, label: c.label, hint: c.where }));
    return withPicked(list, origin.contact_ids, (id) => {
      const c = idx.get(String(id));
      return {
        label: c ? c.name || 'Unnamed' : 'Contact not on file',
        hint: c && isLeftCompany(c) ? 'left company' : 'not on this record',
      };
    });
  }, [record, allAccounts, origin]);
  const peopleOptions = useMemo(() => {
    const names = Array.from(new Set((users || []).map((u) => u && u.name).filter(Boolean))).sort((a, b) => a.localeCompare(b));
    return withPicked(
      names.map((n) => ({ id: n, label: n })),
      origin.internal_attendees,
      (n) => ({ label: n, hint: 'not in the user list' })
    );
  }, [users, origin]);
  const oppOptions = useMemo(() => {
    const list = taggableOpportunities(record, allAccounts, allProjects).map((o) => ({ id: o.id, label: o.label, hint: o.status }));
    return withPicked(list, origin.opportunity_ids, (id) => {
      const p = (allProjects || []).find((x) => x && String(x.id) === String(id));
      return { label: p ? opportunityLabel(p) : id, hint: p ? 'linked to another record' : 'not found' };
    });
  }, [record, allAccounts, allProjects, origin]);

  // never on the GLOBAL KSA tab: it has no CRM accounts to log against
  if (scope) return null;

  const ownerOptions = (extra) =>
    Array.from(new Set([...(users || []).map((u) => u.name).filter(Boolean), ...extra.filter(Boolean)])).sort((a, b) => a.localeCompare(b));
  const set = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    setErr('');
  };
  const setNext = (key, value) => {
    setForm((p) => ({ ...p, next: { ...p.next, [key]: value } }));
    setErr('');
  };
  const toggle = (key) => (id) => setForm((p) => ({ ...p, [key]: toggleId(p[key], id) }));

  const isUpdate = editing || !!saved;
  const uploadedFiles = (saved && saved.uploaded) || [];
  const removedFiles = (saved && saved.removed) || []; // deleted by a save whose uploads then failed
  const existingFiles = editing ? (initial.attachments || []).filter((a) => a && a.id && !removedFiles.includes(a.id)) : [];
  const home = editing
    ? pickField(initial, 'accountId', 'account_id') || (record && record.id)
    : saved
      ? saved.account_id || (record && record.id)
      : record && record.id;

  const addFiles = (fileList) => {
    const picked = Array.from(fileList || []);
    const bad = picked.filter((f) => !fileAllowed(f.name));
    const good = picked.filter((f) => fileAllowed(f.name));
    const key = (f) => `${f.name}|${f.size}`;
    setStaged((cur) => {
      const have = new Set(cur.map(key));
      return [...cur, ...good.filter((f) => !have.has(key(f)))];
    });
    setErr(bad.length ? `That file type can't be attached: ${bad.map((f) => f.name).join(', ')}` : '');
  };

  const save = async () => {
    if (busy) return;
    const f = form;
    if (!String(f.text || '').trim()) {
      setErr('Add a note - what was said, who was there.');
      return;
    }
    if (!home) {
      setErr('There is no record to log this on.');
      return;
    }
    // AL-06: a next step is optional, but once it has a description it needs an owner and a date
    const wantsNext = !isUpdate && !companyWithSites && !hideNextStep && !!f.next.description.trim();
    if (wantsNext && !f.next.owner) {
      setErr('Pick who owns the next step.');
      return;
    }
    if (wantsNext && !f.next.due_date) {
      setErr('Pick a due date for the next step.');
      return;
    }
    const entryId = editing ? initial.id : saved ? saved.id : null;
    const failWord = entryId ? 'Could not save the activity - try again.' : 'Could not log the activity - try again.';
    setBusy(true);
    setErr('');
    try {
      let row;
      try {
        const r = await fetch(entryId ? `/api/accounts/${enc(home)}/activity/${enc(entryId)}` : `/api/accounts/${enc(home)}/activity`, {
          method: entryId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(activityBody(f, { nextStep: wantsNext ? taskPayload(f.next) : null })),
        });
        if (!r.ok) {
          setErr(await readError(r, failWord));
          return;
        }
        row = await r.json().catch(() => ({}));
      } catch (e) {
        console.error('saveActivity error:', e);
        setErr(failWord);
        return;
      }
      // the POST's next_step survives a later retry's PUT (which has none)
      const entry = { ...(saved || {}), ...(row || {}) };
      if (!entry.id) entry.id = entryId;
      if (!entry.account_id) entry.account_id = home;
      if (!entry.id) {
        setErr(failWord);
        return;
      }
      const { uploaded, removed, ...out } = entry; // what the host is told

      // AL-07: files once the entry exists. A failure keeps the entry and names the file.
      const notRemoved = [];
      for (const attId of removing) {
        try {
          const r = await fetch(`${ACTIVITY_FILES_BASE}/attachments/${enc(attId)}`, { method: 'DELETE' });
          if (!r.ok && r.status !== 404) notRemoved.push(attId);
        } catch (e) {
          console.error('removeActivityFile error:', e);
          notRemoved.push(attId);
        }
      }
      const failed = [];
      const done = [];
      for (const file of staged) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('doc_type', 'general');
        try {
          const r = await fetch(`${ACTIVITY_FILES_BASE}/activity/${enc(entry.id)}/attachments`, { method: 'POST', body: fd });
          if (r.ok) done.push(await r.json().catch(() => ({ id: `${file.name}-${done.length}`, filename: file.name, size: file.size })));
          else failed.push({ file, reason: await readError(r, r.status === 413 ? 'the file is too large' : '') });
        } catch (e) {
          console.error('uploadActivityFile error:', e);
          failed.push({ file, reason: '' });
        }
      }
      if (failed.length || notRemoved.length) {
        const removedNow = removing.filter((x) => !notRemoved.includes(x));
        setSaved({ ...entry, uploaded: [...uploadedFiles, ...done], removed: [...removedFiles, ...removedNow] });
        unreported.current = out;
        setStaged(failed.map((x) => x.file));
        setRemoving(notRemoved);
        const nameOf = (id) => (existingFiles.find((a) => a.id === id) || {}).filename || 'a file';
        const parts = [];
        if (failed.length)
          parts.push(
            `these files did not upload: ${failed.map((x) => (x.reason ? `${x.file.name} (${x.reason})` : x.file.name)).join(', ')}`
          );
        if (notRemoved.length) parts.push(`${notRemoved.map(nameOf).join(', ')} could not be removed`);
        setErr(`Saved, but ${parts.join('; and ')}. Press Save to try again, or Close.`);
        return;
      }
      unreported.current = null;
      if (!editing) {
        setForm({ ...blankForm(me, defaults), next: blankNext(me) });
        setStaged([]);
        setSaved(null);
      }
      if (onSaved) onSaved(out);
    } finally {
      setBusy(false);
    }
  };

  // after a partial save the entry exists: closing must still refresh the timeline
  const close = () => {
    if (saved) {
      const { uploaded, removed, ...out } = saved;
      unreported.current = null;
      if (onSaved) onSaved(out);
      return;
    }
    if (onCancel) onCancel();
  };

  return (
    <div style={{ background: '#FAFBFC', borderRadius: 10, padding: 18, marginBottom: 16, border: '1px solid #F0F0F0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: form.type === REVISION_TYPE ? '2fr 1fr 1fr' : '1fr 1fr', gap: 10 }}>
          <div>
            <label style={LABEL}>Type</label>
            <select style={INPUT} value={form.type} onChange={(e) => set('type', e.target.value)}>
              <option value="">-</option>
              {MEETING_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
              {form.type && !MEETING_TYPES.includes(form.type) && <option value={form.type}>{form.type}</option>}
            </select>
          </div>
          {/* AL-05: "Proposal sent (with revision #)" */}
          {form.type === REVISION_TYPE && (
            <div>
              <label style={LABEL}>Revision #</label>
              <input style={INPUT} value={form.revision} onChange={(e) => set('revision', e.target.value)} placeholder="e.g. 2" />
            </div>
          )}
          <div>
            <label style={LABEL}>Date</label>
            <input type="date" style={INPUT} value={form.date} onChange={(e) => set('date', e.target.value)} />
          </div>
        </div>
        <div>
          <label style={LABEL}>Subject</label>
          <input
            style={INPUT}
            value={form.subject}
            onChange={(e) => set('subject', e.target.value)}
            placeholder="e.g. Discovery call - Decatur plant"
          />
        </div>
        <div>
          <label style={LABEL}>Notes</label>
          <textarea
            style={{ ...INPUT, minHeight: 60, resize: 'vertical' }}
            value={form.text}
            onChange={(e) => set('text', e.target.value)}
            placeholder="What was said, who was there"
          />
        </div>
        <div>
          <label style={LABEL}>Recording link (Teams / SharePoint)</label>
          <input
            style={INPUT}
            value={form.recording_url}
            onChange={(e) => set('recording_url', e.target.value)}
            placeholder="https://... (link, never an upload)"
          />
        </div>

        {/* AL-07 attendees + AL-09 tags, on every type */}
        <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
          <CheckList
            label="Customer attendees"
            empty="No contacts on file for this record."
            options={contactOptions}
            picked={form.contact_ids}
            onToggle={toggle('contact_ids')}
          />
          <CheckList
            label="Internal attendees"
            empty="No people in the user list."
            options={peopleOptions}
            picked={form.internal_attendees}
            onToggle={toggle('internal_attendees')}
          />
          <CheckList
            label="Tag to opportunities"
            empty="No opportunities for this record."
            options={oppOptions}
            picked={form.opportunity_ids}
            onToggle={toggle('opportunity_ids')}
          />
        </div>

        {/* AL-07: files - several per entry; recordings stay links */}
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={LABEL}>Files (optional)</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {existingFiles.map((a) => {
              const out = removing.includes(a.id);
              return (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11 }}>
                  <span style={{ fontWeight: 700, color: out ? '#B71C1C' : '#0D0D0D', textDecoration: out ? 'line-through' : 'none' }}>
                    {a.filename}
                  </span>
                  {fileSize(a.size) && <span style={{ color: '#999', fontSize: 10 }}>{fileSize(a.size)}</span>}
                  <button
                    type="button"
                    style={{ ...SMALL_BTN, color: out ? '#1B5E20' : '#B71C1C' }}
                    onClick={() => setRemoving((r) => toggleId(r, a.id))}
                  >
                    {out ? 'Keep' : 'Remove'}
                  </button>
                  {out && <span style={{ fontSize: 10, color: '#B71C1C' }}>deleted when you save</span>}
                </div>
              );
            })}
            {uploadedFiles.map((a) => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11 }}>
                <span style={{ fontWeight: 700, color: '#0D0D0D' }}>{a.filename}</span>
                <span style={{ fontSize: 10, color: '#1B5E20', fontWeight: 700 }}>uploaded</span>
              </div>
            ))}
            {staged.map((file, i) => (
              <div key={`${file.name}|${file.size}|${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11 }}>
                <span style={{ fontWeight: 700, color: '#0D0D0D' }}>{file.name}</span>
                {fileSize(file.size) && <span style={{ color: '#999', fontSize: 10 }}>{fileSize(file.size)}</span>}
                <span style={{ fontSize: 10, color: '#B07503', fontWeight: 700 }}>uploads when you save</span>
                <button
                  type="button"
                  title={`Do not attach ${file.name}`}
                  style={{ ...SMALL_BTN, color: '#B71C1C' }}
                  onClick={() => setStaged((cur) => cur.filter((x) => x !== file))}
                >
                  Remove
                </button>
              </div>
            ))}
            <input
              type="file"
              multiple
              accept={FILE_ACCEPT}
              title="Attach files"
              aria-label="Attach files"
              style={{ fontSize: 10.5, fontFamily: 'Montserrat' }}
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </div>
        </div>

        {/* AL-06: the next step becomes a task on this record in the same save */}
        {!isUpdate && !hideNextStep && (
          <div style={{ gridColumn: '1 / -1', borderTop: '1px dashed #E4E4E4', paddingTop: 12 }}>
            {companyWithSites ? (
              <div style={{ fontSize: 10.5, color: '#888' }}>
                Next steps are tasks, and tasks go on a site - open one of its sites to log a next step.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '3fr 1.3fr 1fr', gap: 10 }}>
                <div>
                  <label style={LABEL}>Next step (optional - creates a task)</label>
                  <input
                    style={INPUT}
                    value={form.next.description}
                    placeholder="e.g. Send budgetary quote to Hong"
                    onChange={(e) => setNext('description', e.target.value)}
                  />
                </div>
                <div>
                  <label style={LABEL}>Owner</label>
                  <select
                    style={{ ...INPUT, appearance: 'auto' }}
                    value={form.next.owner}
                    onChange={(e) => setNext('owner', e.target.value)}
                  >
                    <option value="">-</option>
                    {ownerOptions([form.next.owner]).map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={LABEL}>Due date</label>
                  <input type="date" style={INPUT} value={form.next.due_date} onChange={(e) => setNext('due_date', e.target.value)} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={LABEL} htmlFor="activity-next-tagged">
                    Tag people on the next step (optional)
                  </label>
                  <TagPeople
                    id="activity-next-tagged"
                    value={form.next.tagged}
                    onChange={(v) => setNext('tagged', v)}
                    owner={form.next.owner}
                    people={ownerOptions([])}
                    label="Tag people on the next step"
                  />
                </div>
              </div>
            )}
          </div>
        )}
        {saved && saved.next_step && (
          <div style={{ gridColumn: '1 / -1', fontSize: 10.5, color: '#1B5E20', fontWeight: 700 }}>The next step was added to Tasks.</div>
        )}
      </div>
      {err && (
        <div role="alert" style={{ ...ERROR_LINE, marginTop: 10 }}>
          {err}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          style={{ ...BTN_PRIMARY, opacity: busy ? 0.6 : 1, cursor: busy ? 'wait' : 'pointer' }}
        >
          {busy ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={close} disabled={busy} style={BTN_CANCEL}>
          {saved ? 'Close' : 'Cancel'}
        </button>
      </div>
    </div>
  );
}
